/**
 * PATH: packages/email-hub/src/feature/components/email-reader/useReaderZoom.ts
 * FILE_ID: EMAILHUB-EMAILREADER-ZOOM-HOOK-001
 * PURPOSE: Own persisted, email-scoped reader zoom behavior.
 */
import { useEffect, useRef, useState } from 'react'
import {
  fetchServerState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'

export const ZOOM_MIN = 0.6
export const ZOOM_MAX = 2.5
const ZOOM_STEP = 0.1
const ZOOM_STORAGE_KEY = 'emailhub:body-zoom'

const clampZoom = (zoom: number) =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom))

const readStoredZoom = (): number => {
  if (typeof window === 'undefined') return 1
  try {
    const raw = window.localStorage.getItem(ZOOM_STORAGE_KEY)
    const parsed = raw ? Number.parseFloat(raw) : 1
    return Number.isFinite(parsed) ? clampZoom(parsed) : 1
  } catch {
    return 1
  }
}

export function useReaderZoom() {
  const [bodyZoom, setBodyZoom] = useState(readStoredZoom)
  const scrollRef = useRef<HTMLDivElement>(null)
  const zoomHydratedRef = useRef(false)
  const zoomTouchedRef = useRef(false)

  useEffect(() => {
    try {
      window.localStorage.setItem(ZOOM_STORAGE_KEY, String(bodyZoom))
    } catch {
      // Private mode or quota failures leave zoom session-local.
    }
  }, [bodyZoom])

  useEffect(() => {
    let cancelled = false
    void fetchServerState<{ zoom: number }>(
      'email:reader-zoom',
      { zoom: 1 },
    ).then((state) => {
      if (cancelled || zoomTouchedRef.current) {
        zoomHydratedRef.current = true
        return
      }
      if (
        state &&
        typeof state.zoom === 'number' &&
        Number.isFinite(state.zoom)
      ) {
        setBodyZoom(clampZoom(state.zoom))
      }
      zoomHydratedRef.current = true
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!zoomHydratedRef.current) return
    zoomTouchedRef.current = true
    scheduleServerSave('email:reader-zoom', { zoom: bodyZoom })
  }, [bodyZoom])

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      setBodyZoom((zoom) => clampZoom(zoom - event.deltaY * 0.01))
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [])

  const zoomIn = () =>
    setBodyZoom((zoom) =>
      clampZoom(Math.round((zoom + ZOOM_STEP) * 10) / 10),
    )
  const zoomOut = () =>
    setBodyZoom((zoom) =>
      clampZoom(Math.round((zoom - ZOOM_STEP) * 10) / 10),
    )
  const resetZoom = () => setBodyZoom(1)

  return { bodyZoom, scrollRef, zoomIn, zoomOut, resetZoom }
}
